/* ==========================================================================
   Happy Customers Logo Carousel Component
   Displays infinite smooth scrolling logos of real municipal & partner deployments.
   ========================================================================== */

import { t, getLocale } from '../locales/i18n.js';

const customerTranslations = {
  cs: {
    publicSafety: 'Veřejná bezpečnost',
    infrastructure: 'Infrastruktura',
    integration: 'Integrace',
    energy: 'Energetika',
    smartLighting: 'Chytré osvětlení',
    publicSector: 'Veřejný sektor',
    distribution: 'Distribuce',
    lifeSaved: 'Záchrana života 2023',
    healthcare: 'Zdravotnictví',
    smartCity: 'Chytré město',
    energyUtility: 'Energetika a distribuce',
    municipalPolice: 'Městská policie',
    securityPartner: 'Bezpečnostní partner',
    criticalInfra: 'Kritická infrastruktura',
    capitalPrague: 'Hlavní město Praha',
    ministry: 'Ministerstvo práce a soc. věcí',
    vmsDistributor: 'Distributor VMS',
    hospital: 'Fakultní nemocnice'
  },
  en: {
    publicSafety: 'Public Safety',
    infrastructure: 'Infrastructure',
    integration: 'Integration',
    energy: 'Energy',
    smartLighting: 'Smart Lighting',
    publicSector: 'Public Sector',
    distribution: 'Distribution',
    lifeSaved: 'Life Saved 2023',
    healthcare: 'Healthcare',
    smartCity: 'Smart City',
    energyUtility: 'Energy & Utility',
    municipalPolice: 'Municipal Police',
    securityPartner: 'Security Partner',
    criticalInfra: 'Critical Infrastructure',
    capitalPrague: 'Capital City Prague',
    ministry: 'Ministry of Labour',
    vmsDistributor: 'VMS Distributor',
    hospital: 'University Hospital'
  },
  de: {
    publicSafety: 'Öffentliche Sicherheit',
    infrastructure: 'Infrastruktur',
    integration: 'Integration',
    energy: 'Energie',
    smartLighting: 'Smarte Beleuchtung',
    publicSector: 'Öffentlicher Sektor',
    distribution: 'Distribution',
    lifeSaved: 'Leben gerettet 2023',
    healthcare: 'Gesundheitswesen',
    smartCity: 'Smart City',
    energyUtility: 'Energie & Versorgung',
    municipalPolice: 'Stadtpolizei',
    securityPartner: 'Sicherheitspartner',
    criticalInfra: 'Kritische Infrastruktur',
    capitalPrague: 'Hauptstadt Prag',
    ministry: 'Arbeitsministerium',
    vmsDistributor: 'VMS-Distributor',
    hospital: 'Universitätsklinikum'
  },
  es: {
    publicSafety: 'Seguridad pública',
    infrastructure: 'Infraestructura',
    integration: 'Integración',
    energy: 'Energía',
    smartLighting: 'Iluminación inteligente',
    publicSector: 'Sector público',
    distribution: 'Distribución',
    lifeSaved: 'Vida salvada 2023',
    healthcare: 'Salud',
    smartCity: 'Ciudad Inteligente',
    energyUtility: 'Energía y Servicios',
    municipalPolice: 'Policía Municipal',
    securityPartner: 'Socio de Seguridad',
    criticalInfra: 'Infraestructura Crítica',
    capitalPrague: 'Ciudad de Praga',
    ministry: 'Ministerio de Trabajo',
    vmsDistributor: 'Distribuidor VMS',
    hospital: 'Hospital Universitario'
  },
  pt: {
    publicSafety: 'Segurança Pública',
    infrastructure: 'Infraestrutura',
    integration: 'Integração',
    energy: 'Energia',
    smartLighting: 'Iluminação Inteligente',
    publicSector: 'Setor Público',
    distribution: 'Distribuição',
    lifeSaved: 'Vida Salva 2023',
    healthcare: 'Saúde',
    smartCity: 'Cidade Inteligente',
    energyUtility: 'Energia e Serviços',
    municipalPolice: 'Polícia Municipal',
    securityPartner: 'Parceiro de Segurança',
    criticalInfra: 'Infraestrutura Crítica',
    capitalPrague: 'Cidade de Praga',
    ministry: 'Ministério do Trabalho',
    vmsDistributor: 'Distribuidor VMS',
    hospital: 'Hospital Universitário'
  },
  ar: {
    publicSafety: 'الأمن العام',
    infrastructure: 'البنية التحتية',
    integration: 'التكامل',
    energy: 'الطاقة',
    smartLighting: 'الإنارة الذكية',
    publicSector: 'القطاع العام',
    distribution: 'التوزيع',
    lifeSaved: 'إنقاذ حياة 2023',
    healthcare: 'الرعاية الصحية',
    smartCity: 'مدينة ذكية',
    energyUtility: 'الطاقة والمرافق',
    municipalPolice: 'شرطة البلدية',
    securityPartner: 'شريك أمني',
    criticalInfra: 'بنية تحتية حرجة',
    capitalPrague: 'عاصمة براغ',
    ministry: 'وزارة العمل',
    vmsDistributor: 'موزع VMS',
    hospital: 'مستشفى جامعي'
  }
};

export function renderCustomerCarousel() {
  const section = document.createElement('section');
  section.className = 'sed-section sed-customer-section';
  section.style.backgroundColor = '#ffffff';
  section.style.borderTop = '1px solid var(--slate-line)';
  section.style.borderBottom = '1px solid var(--slate-line)';
  section.style.padding = '3.5rem 0';
  section.style.overflow = 'hidden';

  const lang = getLocale() || 'en';
  const ct = customerTranslations[lang] || customerTranslations.en;

  const customerList = [
    { name: 'Město Pardubice', type: ct.smartCity, badge: ct.publicSafety },
    { name: 'Město Česká Lípa', type: ct.smartCity, badge: ct.publicSafety },
    { name: 'Statutární Město České Budějovice', type: ct.smartCity, badge: ct.publicSafety },
    { name: 'EG.D (E.ON Energy Group)', type: ct.energyUtility, badge: ct.infrastructure },
    { name: 'Statutární Město Plzeň', type: ct.municipalPolice, badge: ct.publicSafety },
    { name: 'Emergency Protection s.r.o.', type: ct.securityPartner, badge: ct.integration },
    { name: 'ČEZ Group', type: ct.criticalInfra, badge: ct.energy },
    { name: 'THMP (Technologie hl. m. Prahy)', type: ct.capitalPrague, badge: ct.smartLighting },
    { name: 'MPSV ČR', type: ct.ministry, badge: ct.publicSector },
    { name: 'Abion Security', type: ct.vmsDistributor, badge: ct.distribution },
    { name: 'Město Dubí', type: ct.smartCity, badge: ct.lifeSaved },
    { name: 'Fakultní Nemocnice Olomouc', type: ct.hospital, badge: ct.healthcare },
    { name: 'Město Tachov', type: ct.smartCity, badge: ct.publicSafety },
    { name: 'Město Boskovice', type: ct.smartCity, badge: ct.publicSafety }
  ];

  // Double array for continuous seamless infinite CSS ticker
  const tickerItems = [...customerList, ...customerList];

  section.innerHTML = `
    <div class="sed-container">
      <div class="sed-section-header" style="margin-bottom: 2rem;">
        <div style="font-size: 0.8rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; color: var(--blue-primary); margin-bottom: 0.5rem;">
          ${t('home.customerEyebrow') || 'PROVEN FIELD DEPLOYMENTS'}
        </div>
        <h2 class="sed-section-title" style="margin-bottom: 0.5rem;">
          ${t('customers.title')}
        </h2>
        <p style="font-size: 1rem; color: var(--text-muted); max-width: 620px; margin: 0 auto;">
          ${t('customers.subtitle')}
        </p>
      </div>

      <!-- Infinite Logo Ticker Container -->
      <div class="sed-ticker-wrapper">
        <div class="sed-ticker-track">
          ${tickerItems.map(item => `
            <div class="sed-ticker-item">
              <div class="sed-customer-logo-card">
                <div class="sed-customer-badge">${item.badge}</div>
                <div class="sed-customer-name">${item.name}</div>
                <div class="sed-customer-type">${item.type}</div>
              </div>
            </div>
          `).join('')}
        </div>
      </div>
    </div>
  `;

  return section;
}
